/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @implements ConvertToTemplateAPI
 */
define(['./api/ConvertToTemplateAPI',
	'@waca/dashboard-common/js/core/APIFactory',
	'text!../../../dashboard/schema/schemaDefs.json',
	'text!../../../dashboard/schema/templateSchemaMappings.json',
	'text!../../../dashboard/schema/nameSchema.json',
	'text!../../../dashboard/schema/layoutSchema.json',
	'text!../../../dashboard/schema/themeSchema.json',
	'text!../../../dashboard/schema/versionSchema.json',
	'text!../../../dashboard/schema/eventGroupsSchema.json',
	'text!../../../dashboard/schema/propertiesSchema.json',
	'text!../../../dashboard/schema/widgetsTemplateSchema.json'
], (ConvertToTemplateAPI,
	APIFactory,
	SchemaDefs,
	SchemaMappings,
	NameSchema,
	LayoutSchema,
	ThemeSchema,
	VersionSchema,
	EventGroupsSchema,
	PropertiesSchema,
	WidgetsTemplateSchema
) => {

	class ConvertToTemplate {

		constructor({ features }) {
			this.dashboard = features.API;
			this._featureChecker = this.dashboard.getGlassCoreSvc('.FeatureChecker');
		}

		getAPI() {
			const isEnabled = !this._featureChecker.checkValue('dashboard', 'dashboardTemplates', 'disabled');
			if (!isEnabled && !this._api) {
				// A fake API that does nothing
				this._api = { convertToTemplate: () => Promise.resolve() };
			}

			if (!this._api) {
				this._api = APIFactory.createAPI(this, [ConvertToTemplateAPI]);
			}

			return this._api;
		}

		/**
		 * Extracts a template JSON spec out of an existing dashboard spec
		 * @param {object} dashboardSpec - dashboard spec to extract a template from
		 * @returns returns a JSON object representing a template of the input dashboard spec
		 */
		convertToTemplate(dashboardSpec) {
			if (!dashboardSpec || !Object.keys(dashboardSpec).length) {
				return Promise.resolve(null);
			}

			return this._getAjv().then((ajv) => {
				const templateSchmaMappings = this._getSchemaMappings();
				const templateSpec = {};

				for (let propertyName of Object.keys(dashboardSpec)) {
					let propertySchema = templateSchmaMappings[propertyName];
					if (!propertySchema) {
						continue;
					}
					let propertyValue = dashboardSpec[propertyName];
					let removeNonTemplateProperties = ajv.getSchema(propertySchema);
					if (!removeNonTemplateProperties) {
						continue;
					}
					removeNonTemplateProperties(propertyValue);
					if (propertyName === 'layout' && !Array.isArray(propertyValue)) {
						this._lockVisualizationType(propertyValue.items);
					}
					templateSpec[propertyName] = propertyValue;
				}
				return templateSpec;
			});
		}

		_lockVisualizationType(items) {
			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				if (item.items) {
					this._lockVisualizationType(item.items);
				}
				if (item.type === 'widget' &&
					item.features &&
					item.features.Models_internal &&
					item.features.Models_internal.type === 'live'
				) {
					item.features.Models_internal.visTypeLocked = true;
				}
			}
		}

		_getSchemaMappings() {
			if (!this.schemaMappings) {
				this.schemaMappings = JSON.parse(SchemaMappings);
			}
			return this.schemaMappings;
		}

		_getAjv() {
			if (this.ajv) {
				return Promise.resolve(this.ajv);
			}
			return new Promise((resolve, reject) => {
				require(['ajv'], (Ajv) => {
					try {
						this.ajv = new Ajv({
							verbose: true,
							allErrors: true,
							removeAdditional: 'all',
							schemas:[
								JSON.parse(SchemaDefs),
								JSON.parse(NameSchema),
								JSON.parse(LayoutSchema),
								JSON.parse(ThemeSchema),
								JSON.parse(VersionSchema),
								JSON.parse(EventGroupsSchema),
								JSON.parse(PropertiesSchema),
								JSON.parse(WidgetsTemplateSchema)
							]
						});
						resolve(this.ajv);
					} catch (err) {
						reject(err);
					}
				}, reject);
			});
		}
	}
	return ConvertToTemplate;
});
